import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import dayjs from 'dayjs';
import durationPlugin from 'dayjs/plugin/duration';

dayjs.extend(durationPlugin);

const MILLISECONDS_IN_DAY = 24 * 60 * 60 * 1000;
const UPDATE_INTERVAL = 1000;

interface OfferBannerWithTimerProps {
  offerEndDate: string | undefined;
  className?: string;
}

const OfferBannerWithTimer: React.FC<OfferBannerWithTimerProps> = ({
  offerEndDate,
  className,
}) => {
  const [displayTime, setDisplayTime] = useState('');
  const lastDisplayRef = useRef('');
  const intervalRef = useRef<NodeJS.Timeout>();

  const targetDate = useMemo(() => {
    if (offerEndDate && dayjs(offerEndDate).isValid()) {
      return dayjs(offerEndDate).endOf('day');
    }
    return undefined;
  }, [offerEndDate]);

  const updateTimer = useCallback(() => {
    if (!targetDate) {
      setDisplayTime('');
      return;
    }
    const now = dayjs();
    const diffMs = targetDate.diff(now);

    let newDisplay = '';

    if (diffMs <= 0) {
      newDisplay = 'Expired';
    } else if (diffMs > 2 * MILLISECONDS_IN_DAY) {
      const daysLeft = Math.ceil(diffMs / MILLISECONDS_IN_DAY);
      newDisplay = `Ends in ${daysLeft} day${daysLeft !== 1 ? 's' : ''}`;
    } else {
      const d = dayjs.duration(diffMs);
      const hh = String(Math.floor(d.asHours())).padStart(2, '0');
      const mm = String(d.minutes()).padStart(2, '0');
      const ss = String(d.seconds()).padStart(2, '0');
      newDisplay = `Ends in ${hh}:${mm}:${ss}`;
    }

    if (newDisplay !== lastDisplayRef.current) {
      lastDisplayRef.current = newDisplay;
      setDisplayTime(newDisplay);
    }
  }, [targetDate]);

  useEffect(() => {
    updateTimer();
    if (targetDate && targetDate.diff(dayjs()) > 0) {
      intervalRef.current = setInterval(updateTimer, UPDATE_INTERVAL);
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = undefined;
      }
    };
  }, [updateTimer, targetDate]);

  if (!offerEndDate || !displayTime || displayTime === 'Expired') return null;

  return <span className={className}>{displayTime}</span>;
};

export default React.memo(OfferBannerWithTimer);
